import { useMemo } from "react";
import Avatar from "../components/Avatar";
import Button from "../components/Button";
import CodeFelder from "../components/CodeFelder";
import Icon from "../components/icons/Icon";
import Notiz from "../components/Notiz";
import PersonenListe from "../components/PersonenListe";
import TextFeld from "../components/TextFeld";
import { farbNummern } from "../spieler";
import "./JoinScreen.css";

/** Mitspielen: Code eingeben, dann Namen — oder den alten Platz zurueckholen. */
export default function JoinScreen({
  room,
  joinInfo,
  joinCode,
  setJoinCode,
  joinName,
  setJoinName,
  joinBusy,
  onEnterCode,
  onAbbrechen,
}) {
  // Freie Plaetze von Leuten, die rausgeflogen sind — dort kann man wieder
  // einsteigen, wenn das Token weg ist (anderer Browser, Cache geleert).
  // Bewusst aus dem LAUFENDEN Raumzustand statt aus der Momentaufnahme von
  // /info: die veraltete sofort, und wer sich neu verband, sah sich selbst
  // noch als "online" und bekam seinen eigenen Namen nicht angeboten.
  const alleSpieler = room.room?.players || joinInfo?.players || [];
  const freiePlaetze = alleSpieler.filter((p) => !p.connected && !p.isHost);
  const belegte = alleSpieler.filter((p) => p.connected && !p.isHost);
  const name = joinName.trim();
  // Vorschau mit der Farbe, die man im Spiel haben wird
  const namenSchluessel = [...alleSpieler.map((p) => p.name), name || "?"].join("\n");
  const farbe = useMemo(() => farbNummern(namenSchluessel.split("\n")), [namenSchluessel]);
  const verbunden = room.status === "connected";

  return (
    <div className="spiel beitritt">
      <header className="kopf-zurueck">
        <button type="button" className="ikon-knopf" onClick={onAbbrechen} aria-label="Zurück" title="Zurück">
          <Icon name="zurueck" />
        </button>
        <h1>Mitspielen</h1>
        <span className="platz" />
      </header>

      {!joinInfo ? (
        <main className="spiel-mitte oben">
          {room.kicked && (
            <Notiz art="fehler">
              <b>Du wurdest aus dem Raum entfernt.</b> Mit dem Code kommst du wieder rein.
            </Notiz>
          )}
          {room.error && <Notiz art="fehler">{room.error}</Notiz>}
          <div className="schritt-kopf">
            <span className="ueberzeile gruen">Schritt 1 von 2</span>
            <h2>Code vom Host</h2>
            <p>Vier Zeichen – stehen beim Host unter dem QR-Code.</p>
          </div>
          <CodeFelder wert={joinCode} onChange={setJoinCode} onFertig={onEnterCode} autoFocus />
          <Button variant="haupt" disabled={joinCode.length !== 4 || joinBusy} onClick={() => onEnterCode(joinCode)}>
            {joinBusy ? "Suche Raum…" : "Weiter"}
            {!joinBusy && <Icon name="pfeil" />}
          </Button>
          <p className="hinweis-text">Oder den QR-Code beim Host scannen – dann geht's direkt zum Namen.</p>
        </main>
      ) : (
        <main className="spiel-mitte oben">
          {room.error && <Notiz art="fehler">{room.error}</Notiz>}
          <span className="code-gefunden">
            <b>{joinInfo.code}</b>Raum gefunden
          </span>
          <div className="schritt-kopf">
            <span className="ueberzeile gruen">Schritt 2 von 2</span>
            <h2>Wie heißt du?</h2>
          </div>
          <Avatar name={name || "?"} farbe={farbe(name || "?")} groesse="riesig" />
          <TextFeld
            value={joinName}
            onChange={(e) => setJoinName(e.target.value.slice(0, 20))}
            placeholder="Dein Name"
            maxLength={20}
            aria-label="Dein Name"
            autoComplete="nickname"
            onKeyDown={(e) => {
              if (e.key === "Enter" && name && verbunden) room.submitName(name);
            }}
          />
          <Button variant="haupt" disabled={!name || !verbunden} onClick={() => room.submitName(name)}>
            {verbunden ? "Beitreten" : "Verbinde…"}
            {verbunden && <Icon name="pfeil" />}
          </Button>

          {freiePlaetze.length > 0 && (
            <section className="abschnitt">
              <h2>Schon mal dabei gewesen?</h2>
              <PersonenListe
                farbe={farbe}
                personen={freiePlaetze.map((p) => ({ key: p.id, name: p.name }))}
                aktion={(p) => (
                  <Button variant="neben" onClick={() => room.claimSlot(p.key)}>
                    Platz zurückholen
                  </Button>
                )}
              />
            </section>
          )}
          {/* Wer belegt ist, ist wirklich noch im Spiel. Ohne diese Zeile
              raetselt man, warum der eigene Name nicht auftaucht. */}
          {belegte.length > 0 && (
            <p className="hinweis-text beitritt-belegt">Gerade im Spiel: {belegte.map((p) => p.name).join(", ")}</p>
          )}
        </main>
      )}
    </div>
  );
}
